export interface CreateStripeSubscriptionResponse {
  success: boolean;
  subscriptionStatus?: "ACTIVE" | "EXPIRED" | "CANCELED";
  expirationDate?: string;
  message?: string;
  credits?: number; // Updated credits after payment
  subscription?: string; // Subscription type (standard, premium, booster_5, booster_25)
  stripeSubscriptionId?: string; // Stripe subscription ID (for subscriptions)
  stripePaymentIntentId?: string; // Stripe payment intent ID (for one-time payments)
}

/**
 * Create Stripe Subscription or One-time Payment
 *
 * @param accessAuth0Token - Auth0 access token
 * @param paymentMethodId - Stripe Payment Method ID (from Stripe.js)
 * @param productId - Product ID to purchase (e.g., 'league_connect_standard_monthly')
 * @param email - Optional user email (for creating Stripe customer)
 * @param promotionCodeId - Optional Stripe Promotion Code ID (from applyPromoCode)
 * @returns Response with subscription/payment details
 */
export const createStripeSubscription = async (
  accessAuth0Token: string,
  paymentMethodId: string,
  productId: string,
  email?: string,
  promotionCodeId?: string
): Promise<CreateStripeSubscriptionResponse> => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/payments/create-stripe-subscription`;
  try {
    const body: any = { paymentMethodId, productId };
    if (email) body.email = email;
    if (promotionCodeId) body.promotionCodeId = promotionCodeId;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessAuth0Token}`,
      },
      body: JSON.stringify(body),
      credentials: "include",
    });

    const data = (await response.json()) as CreateStripeSubscriptionResponse;
    if (!response.ok) {
      // Clean error message - remove technical details
      let errorMessage = data?.message || "Payment failed";

      // Remove technical details
      errorMessage = errorMessage.split("\n")[0].split("at ")[0].split("/Users/")[0].split("backend/")[0].split("Invalid")[0].split("invocation")[0].trim();

      // If still too technical, use generic message
      if (!errorMessage || errorMessage.length < 5 || errorMessage.includes("prisma") || errorMessage.includes("subscription.findFirst")) {
        errorMessage = "Payment failed. Please try again or contact support.";
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
    return data;
  } catch (error) {
    console.error("createStripeSubscription error:", error);
    return { success: false, message: "Network error. Please check your connection and try again." };
  }
};
